import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SRSPropertyType } from '@/lib/shared-data';
import { Property } from '@/store/properties-slice';

interface TouchpointProgress {
  touchpointId: string;
  totalStandards: number;
  answeredStandards: number;
}

interface AssessmentSidebarProps {
  propertyData: SRSPropertyType & { actualProperty?: Property };
  selectedDepartmentId: string | null;
  selectedTouchpointId: string | null;
  onDepartmentClick: (departmentId: string) => void;
  onTouchpointClick: (touchpointId: string) => void;
  touchpointProgress?: TouchpointProgress[];
  overallCompletionPercentage?: number;
  className?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function AssessmentSidebar({
  propertyData,
  selectedDepartmentId,
  selectedTouchpointId,
  onDepartmentClick,
  onTouchpointClick,
  touchpointProgress = [],
  overallCompletionPercentage = 0,
  className,
  isOpen = true,
  onToggle,
}: AssessmentSidebarProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Function to get bullet color based on completion status
  const getBulletColor = (touchpointId: string) => {
    const progress = touchpointProgress.find(
      tp => tp.touchpointId === touchpointId
    );
    if (!progress || progress.totalStandards === 0) {
      return 'bg-gray-400'; // Gray if no progress data or no standards
    }

    if (progress.answeredStandards === 0) {
      return 'bg-gray-400'; // Gray if no standards answered
    } else if (progress.answeredStandards === progress.totalStandards) {
      return 'bg-green-500'; // Green if all standards answered
    } else {
      return 'bg-red-500'; // Red if partially answered
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed lg:relative lg:translate-x-0 top-0 left-0 h-full w-80 lg:w-72 bg-white border-r border-gray-200 overflow-y-auto transition-transform duration-300 ease-in-out z-50',
          isRTL ? 'right-0 left-auto' : 'left-0 right-auto',
          isOpen
            ? 'translate-x-0'
            : isRTL
              ? 'translate-x-full'
              : '-translate-x-full',
          className
        )}
      >
        {/* Mobile close button */}
        <div className="lg:hidden flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">
            {t('assessments.perform.navigation')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-3">
          {/* Property Type Header */}
          <div className="bg-blue-50 rounded-lg p-3 mb-4">
            <h2 className="text-blue-900 text-sm font-medium mb-1 truncate">
              {propertyData.actualProperty?.name || 'Grand Hotel Riyadh'}
            </h2>

            <div className="mt-2 flex items-center justify-between">
              <span className="text-blue-800 text-xs font-medium">
                {t('assessments.perform.completion', 'Completion')}
              </span>
              <span className="text-blue-900 text-sm font-bold">
                {Math.round(overallCompletionPercentage)}%
              </span>
            </div>

            {/* Progress bar */}
            <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${overallCompletionPercentage}%` }}
              />
            </div>
          </div>

          {/* Departments List */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-900 mb-2 truncate">
              {t('assessments.perform.departments')}
            </h3>

            {propertyData.departments.map(department => (
              <Card key={department.id} className="overflow-hidden">
                <CardHeader
                  className={cn(
                    'pb-2 cursor-pointer transition-colors hover:bg-gray-50 active:bg-gray-100 p-3 min-h-[44px] flex items-center',
                    selectedDepartmentId === department.id
                      ? 'bg-primary/10'
                      : ''
                  )}
                  onClick={() => {
                    onDepartmentClick(department.id);
                    // Auto-select first touchpoint when department is opened
                    if (selectedDepartmentId !== department.id && department.touchpoints.length > 0) {
                      onTouchpointClick(department.touchpoints[0].id);
                    }
                  }}
                >
                  <CardTitle
                    className={cn(
                      'text-xs font-medium flex items-center justify-between',
                      isRTL ? 'flex-row-reverse' : 'flex-row'
                    )}
                  >
                    <span className="truncate flex-1 mr-2">
                      {department.name}
                    </span>
                    <ChevronRight
                      className={cn(
                        'w-3 h-3 transition-transform flex-shrink-0',
                        selectedDepartmentId === department.id
                          ? 'rotate-90'
                          : '',
                        isRTL ? 'rotate-180' : ''
                      )}
                    />
                  </CardTitle>
                </CardHeader>

                {selectedDepartmentId === department.id && (
                  <CardContent className="pt-0">
                    {/* Touchpoints */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-medium text-gray-700 truncate">
                        {t('assessments.perform.touchpoints')}
                      </h4>
                      {department.touchpoints.map(touchpoint => (
                        <Button
                          key={touchpoint.id}
                          variant={
                            selectedTouchpointId === touchpoint.id
                              ? 'default'
                              : 'ghost'
                          }
                          size="sm"
                          onClick={() => onTouchpointClick(touchpoint.id)}
                          className={cn(
                            'w-full justify-start text-xs min-h-[44px] h-11 px-3 transition-colors touch-manipulation',
                            selectedTouchpointId === touchpoint.id
                              ? 'bg-primary text-primary-foreground'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100'
                          )}
                        >
                          <div
                            className={cn(
                              'w-3 h-3 rounded-full mr-1 flex-shrink-0',
                              getBulletColor(touchpoint.id)
                            )}
                          ></div>
                          <span className="truncate">{touchpoint.name}</span>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
